import { auth, db, functions, saveTokenToFirestore } from "../firebase";
import { getIpLocation } from "../services/ipLocation";
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteField,
  arrayUnion,
  arrayRemove,
  increment,
  query,
  orderBy,
  onSnapshot,
  getDocs,
  addDoc,
  where,
  serverTimestamp,
  Timestamp,
  documentId,
  runTransaction,
} from "firebase/firestore";

export const requestNotificationPermission = async (messaging) => {
  try {
    const permission = await Notification.requestPermission();
    if (permission === "granted") {
      const token = await messaging.getToken({
        vapidKey:
          "BDCKHSBv7FSlxFnOWseQBcrlmlxsDc0KJKRpUJnyDwCraKIslf8qS2wvDUyASjp2ZO6Mg-_gcB0ZnMOrK-d-Kg4",
      });

      if (token) {
        await saveTokenToFirestore(token);
      } else {
        console.error("No se pudo obtener el token FCM");
      }
    } else {
      console.error("Permiso de notificaciones denegado");
    }
  } catch (error) {
    console.error("Error al obtener el token FCM:", error);
  }
};

// Fallback sin permisos de geolocalización: infiere el país por IP para
// publicaciones no "worldwide" cuando no pedimos coordenadas GPS en el flujo.
export const getCountryFromIP = async () => {
  const location = await getIpLocation();
  return location?.countryName || null;
};

// Reverse geocoding vía Nominatim (OpenStreetMap), igual que geocodeAddress/
// searchAddressSuggestions en eventsUtils.js: no requiere API key. La API de
// Geocoding de Google no acepta keys con restricción de referrer HTTP (la
// única compatible con nuestra key de Firebase para uso desde el browser),
// así que usarla acá siempre devuelve REQUEST_DENIED.
export const getCountryFromCoordinates = async (latitude, longitude) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=3&addressdetails=1`,
      { headers: { "Accept-Language": "en" } }
    );
    const data = await response.json();
    const country = data?.address?.country;
    if (!country) {
      console.error("Error al obtener el país: la respuesta de reverse geocoding no incluyó country", data);
      return null;
    }
    return country;
  } catch (error) {
    console.error("Error al obtener el país:", error);
    return null;
  }
};

export const formatDate = (date) => {
  if (date instanceof Date) {
    return date.toLocaleDateString();
  } else if (date && date.toDate instanceof Function) {
    return date.toDate().toLocaleDateString();
  }
  return "";
};

// ---------- Venta de ofertas por PayPal ----------
// Si el vendedor activó "Cobrar con PayPal" al publicar (ver publishOffer.js),
// la oferta guarda paypalEmail y se compra en /offers/:id/checkout. El pago
// va 100% al PayPal del vendedor; la comisión (5%) se paga aparte y, igual que
// en tiendas, mientras esté pendiente no se puede publicar otra oferta.
export const OFFER_COMMISSION_RATE = 0.05;

// Comisión todavía no pagada de las ofertas de un vendedor: mismo criterio que
// getPendingCommission (storesUtils.js) pero sobre offerSales.
export const getPendingOfferCommission = async (sellerId) => {
  if (!sellerId) return { amount: 0, salesCount: 0 };
  const asDate = (value) => (value?.toDate ? value.toDate() : value ? new Date(value) : null);
  try {
    const paymentsSnap = await getDocs(
      query(collection(db, "offerCommissionPayments"), where("sellerId", "==", sellerId))
    );

    let lastCoveredUpTo = null;
    paymentsSnap.forEach((docSnap) => {
      const coveredUpTo = asDate(docSnap.data().coveredUpTo);
      if (coveredUpTo && (!lastCoveredUpTo || coveredUpTo > lastCoveredUpTo)) {
        lastCoveredUpTo = coveredUpTo;
      }
    });

    const salesSnap = await getDocs(
      query(collection(db, "offerSales"), where("sellerId", "==", sellerId))
    );

    let amount = 0;
    let salesCount = 0;
    salesSnap.forEach((docSnap) => {
      const sale = docSnap.data();
      const purchaseDate = asDate(sale.purchaseDate);
      if (lastCoveredUpTo && purchaseDate && purchaseDate <= lastCoveredUpTo) return;
      amount += Number(sale.commissionAmount) || 0;
      salesCount += 1;
    });

    return { amount, salesCount };
  } catch (error) {
    console.error("Error al calcular la comisión pendiente de ofertas:", error);
    return { amount: 0, salesCount: 0 };
  }
};

// Lo registra recordPayPalPayment después de verificar el pago con PayPal.
export const markOfferCommissionPaid = async ({ sellerId, paypalTransactionId }) => {
  await functions.httpsCallable("recordPayPalPayment")({
    kind: "offerCommission",
    targetId: sellerId,
    orderId: paypalTransactionId,
  });
};

export const offerSellsWithPaypal = (offer) =>
  !!(offer?.paypalEmail && Number(offer?.price) > 0);

// Precio a cobrar: precio de lista menos el % de descuento de la oferta.
export const getOfferFinalPrice = (offer) => {
  const price = Number(offer?.price) || 0;
  const discount = Math.min(Math.max(Number(offer?.offer) || 0, 0), 100);
  return Number((price * (1 - discount / 100)).toFixed(2));
};

export const getDomainFromUrl = (url) => {
  if (!url) {
    return url;
  }

  if (!url.startsWith("http://") && !url.startsWith("https://")) {
    url = "http://" + url;
  }
  const domain = new URL(url).hostname;
  const dotIndex = domain.lastIndexOf(".");

  if (dotIndex === -1) {
    return domain;
  }

  const shortenedDomain = domain.substring(0, dotIndex);

  if (shortenedDomain.length > 15) {
    return shortenedDomain.slice(0, 15) + "...";
  }

  return shortenedDomain;
};

// Usa una transacción (lee y escribe atómicamente) en vez de getDoc + updateDoc
// sueltos: con eso, dos clicks casi simultáneos (doble click, o el doble
// evento touch+click que disparan algunos navegadores mobile) podían leer el
// mismo estado "todavía no me gusta" antes de que el primer updateDoc
// terminara, y los dos ejecutaban increment(1) — el contador subía de a 2 en
// vez de 1 aunque el array "likes" (arrayUnion, idempotente) quedara bien.
// Con la transacción, Firestore reintenta automáticamente la segunda si hay
// conflicto, y como para entonces ya lee el "likes" actualizado, el segundo
// click alterna a "sacar el me gusta" en vez de duplicar el incremento.
export const handleMeSirve = async (
  offerId,
  userAuthenticated,
  userId,
  setOffers,
  setUserLikes
) => {
  if (!userAuthenticated) {
    return;
  }

  const offerRef = doc(db, "offers", offerId);
  const userLikesRef = doc(db, "userLikes", userId);

  try {
    let nowLiked = false;

    await runTransaction(db, async (transaction) => {
      const offerDoc = await transaction.get(offerRef);
      const currentLikes = offerDoc.data()?.likes || [];
      nowLiked = !currentLikes.includes(userId);

      if (nowLiked) {
        transaction.update(offerRef, {
          likes: arrayUnion(userId),
          helpedCount: increment(1),
        });
        transaction.set(userLikesRef, { [offerId]: true }, { merge: true });
      } else {
        transaction.update(offerRef, {
          likes: arrayRemove(userId),
          helpedCount: increment(-1),
        });
        transaction.set(userLikesRef, { [offerId]: deleteField() }, { merge: true });
      }
    });

    setOffers((prevOffers) =>
      prevOffers.map((offer) =>
        offer.id === offerId
          ? { ...offer, helpedCount: offer.helpedCount + (nowLiked ? 1 : -1) }
          : offer
      )
    );
    setUserLikes((prevLikes) => ({
      ...prevLikes,
      [offerId]: nowLiked,
    }));
  } catch (error) {
    console.error("Error al actualizar 'Me sirve':", error);
  }
};

export const handleFollow = async (
  companyId,
  uid,
  following,
  setFollowing,
  setLoading
) => {
  try {
    setLoading(true);
    const userFollowingRef = doc(db, "userFollowing", uid);
    const companyFollowersRef = doc(db, "companyFollowers", companyId);

    const isFollowing = following.includes(companyId);

    if (isFollowing) {
      await updateDoc(userFollowingRef, {
        [companyId]: deleteField(),
      });
      await updateDoc(companyFollowersRef, {
        [uid]: deleteField(),
      });
      setFollowing((prev) => prev.filter((id) => id !== companyId));
    } else {
      await setDoc(userFollowingRef, { [companyId]: true }, { merge: true });
      await setDoc(companyFollowersRef, { [uid]: true }, { merge: true });
      setFollowing((prev) => [...prev, companyId]);
    }
    setLoading(false);
  } catch (error) {
    console.error("Error al manejar el seguimiento de la empresa:", error);
    setLoading(false);
  }
};

// A diferencia de handleFollow (que alterna seguir/dejar de seguir según el
// estado actual), esta siempre deja de seguir, sin importar el estado previo.
// Hace falta porque "Dejar de seguir" en el menú de tres puntitos se muestra
// siempre (a pedido del usuario, igual que en producción) — con el toggle,
// tocarlo cuando en realidad no seguías a la cuenta terminaba siguiéndola.
export const unfollowCompany = async (companyId, uid, setFollowing) => {
  if (!companyId || !uid) return;
  try {
    const userFollowingRef = doc(db, "userFollowing", uid);
    const companyFollowersRef = doc(db, "companyFollowers", companyId);
    await updateDoc(userFollowingRef, { [companyId]: deleteField() });
    await updateDoc(companyFollowersRef, { [uid]: deleteField() });
    setFollowing((prev) => prev.filter((id) => id !== companyId));
  } catch (error) {
    console.error("Error al dejar de seguir:", error);
  }
};

export const filterOffersByCountry = async (
  coordinates,
  offers,
  setFilteredOffers
) => {
  if (!coordinates) {
    setFilteredOffers(offers);
    return;
  }

  const country = await getCountryFromCoordinates(
    coordinates.latitude,
    coordinates.longitude
  );

  if (!country) {
    setFilteredOffers(offers);
    return;
  }

  // Las ofertas "worldwide" se guardan sin country (ver publishOffer.js) y
  // deben verse desde cualquier país.
  const filtered = offers.filter((offer) => !offer.country || offer.country === country);
  setFilteredOffers(filtered);
};

export const fetchUserLikes = async (userAuthenticated, setUserLikes) => {
  if (userAuthenticated && auth.currentUser) {
    const userLikesRef = doc(db, "userLikes", auth.currentUser.uid);
    const userLikesDoc = await getDoc(userLikesRef);
    if (userLikesDoc.exists()) {
      setUserLikes(userLikesDoc.data());
    }
  }
};

export const fetchFollowing = async (setFollowing, setLoading) => {
  try {
    const { uid } = auth.currentUser || {};
    if (uid) {
      const userFollowingRef = doc(db, "userFollowing", uid);
      const userFollowingDoc = await getDoc(userFollowingRef);

      if (userFollowingDoc.exists()) {
        const followingData = userFollowingDoc.data();
        const followingIds = Object.keys(followingData);
        setFollowing(followingIds);
      } else {
        setFollowing([]);
      }
    }
    setLoading(false);
  } catch (error) {
    console.error("Error al obtener las empresas seguidas:", error);
    setLoading(false);
  }
};

// "No me interesa": oculta una oferta puntual del feed de quien la oculta,
// sin afectar a nadie más ni tocar la oferta en sí. Mismo patrón doc-por-usuario
// que blockedUsers ({ [offerId]: true }).
export const hideOffer = async (userId, offerId) => {
  if (!userId || !offerId) return false;
  try {
    await setDoc(doc(db, "hiddenOffers", userId), { [offerId]: true }, { merge: true });
    return true;
  } catch (error) {
    console.error("Error al ocultar la oferta:", error);
    return false;
  }
};

export const fetchHiddenOfferIds = async (userId) => {
  if (!userId) return [];
  try {
    const snap = await getDoc(doc(db, "hiddenOffers", userId));
    return snap.exists() ? Object.keys(snap.data()) : [];
  } catch (error) {
    console.error("Error al obtener ofertas ocultas:", error);
    return [];
  }
};

// Reporte genérico (ofertas y eventos comparten la colección "reports", ver
// AdminPanel.js que la lee con targetType/targetId/companyId/reporterId).
export const reportItem = async ({ reporterId, targetType, targetId, companyId }) => {
  if (!reporterId || !targetId) return false;
  try {
    await addDoc(collection(db, "reports"), {
      reporterId,
      targetType,
      targetId,
      companyId: companyId || null,
      status: "pending",
      createdAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.error("Error al reportar:", error);
    return false;
  }
};

// Cuentas oficiales de gobiernos/organismos habilitadas (ver AdminPanel.js):
// se leen en vivo como las verificadas, así deshabilitar una saca la insignia.
export const fetchOfficialUids = async () => {
  if (!auth.currentUser) return [];
  try {
    const q = query(collection(db, "officialAccounts"), where("enabled", "==", true));
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.id);
  } catch (error) {
    console.error("Error al obtener las cuentas oficiales:", error);
    return [];
  }
};

// Cuentas verificadas (chequeo en vivo, no un valor guardado en la oferta):
// así la insignia refleja el estado actual de verificación, incluso en
// ofertas publicadas antes de que la cuenta se verificara o después de que
// se le haya revocado.
export const fetchVerifiedUids = async () => {
  if (!auth.currentUser) return [];
  try {
    const q = query(collection(db, "users"), where("verified", "==", true));
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.id);
  } catch (error) {
    console.error("Error al obtener las cuentas verificadas:", error);
    return [];
  }
};

// "Guardados": lista privada de ofertas para revisar después, separada del
// "Me sirve" (userLikes), que es público y suma al ranking de la oferta.
export const toggleSaveOffer = async (userId, offerId, isSaved) => {
  if (!userId || !offerId) return false;
  try {
    const savedRef = doc(db, "savedOffers", userId);
    if (isSaved) {
      await updateDoc(savedRef, { [offerId]: deleteField() });
    } else {
      await setDoc(savedRef, { [offerId]: true }, { merge: true });
    }
    return true;
  } catch (error) {
    console.error("Error al guardar/quitar la oferta:", error);
    return false;
  }
};

export const fetchSavedOfferIds = async (userId) => {
  if (!userId) return [];
  try {
    const savedRef = doc(db, "savedOffers", userId);
    const savedDoc = await getDoc(savedRef);
    return savedDoc.exists() ? Object.keys(savedDoc.data()) : [];
  } catch (error) {
    console.error("Error al obtener las ofertas guardadas:", error);
    return [];
  }
};

// Trae las ofertas completas por ID (a diferencia de fetchSavedOfferIds, que
// solo da los IDs): las guardadas pueden ser de cualquier empresa, no solo
// las del perfil que se está mirando, así que hace falta esta consulta aparte.
export const fetchOffersByIds = async (offerIds) => {
  const uniqueIds = [...new Set(offerIds)].filter(Boolean);
  const results = [];
  for (let i = 0; i < uniqueIds.length; i += 30) {
    const chunk = uniqueIds.slice(i, i + 30);
    try {
      const snap = await getDocs(
        query(collection(db, "offers"), where(documentId(), "in", chunk)),
      );
      snap.forEach((docSnap) => {
        results.push({ id: docSnap.id, ...docSnap.data() });
      });
    } catch (error) {
      console.error("Error al obtener las ofertas guardadas:", error);
    }
  }
  return results;
};

export const formatCount = (count) => {
  const n = count || 0;
  return new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(n);
};

// offer.price es el precio de lista (sin descuento) que carga quien publica
// la oferta; offer.offer es el % que el cupón descuenta sobre ese precio.
export const getOfferPricing = (offer) => {
  const previousPrice = Number(offer.price);
  const discountPercent = Number(offer.offer);
  if (!previousPrice || !discountPercent || discountPercent <= 0 || discountPercent >= 100) {
    return null;
  }
  const finalPrice = previousPrice * (1 - discountPercent / 100);
  const savings = previousPrice - finalPrice;
  return { previousPrice, finalPrice, savings };
};

export const fetchHelpersInfo = async (uids) => {
  const results = {};
  const uniqueUids = [...new Set(uids)].filter(Boolean);

  for (let i = 0; i < uniqueUids.length; i += 30) {
    const chunk = uniqueUids.slice(i, i + 30);
    try {
      const snap = await getDocs(
        query(collection(db, "users"), where(documentId(), "in", chunk)),
      );
      snap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        results[docSnap.id] = {
          name: data.name || data.displayName || "Usuario",
          profileImageUrl: data.profileImageUrl || "",
        };
      });
    } catch (error) {
      console.error("Error al obtener datos de los usuarios que dieron 'Me sirve':", error);
    }
  }

  return results;
};

// --- Existing functions ---
// requestNotificationPermission, getCountryFromCoordinates, formatDate, getDomainFromUrl,
// handleMeSirve, handleFollow, filterOffersByCountry, fetchUserLikes, fetchFollowing

// Arma el objeto de oferta que usa Home a partir de un documento de
// Firestore (lo usa usePagedFeed en home.js; antes vivía adentro de
// fetchOffersAndStats, que escuchaba la colección entera).
export const mapOfferDoc = (doc) => {
  const data = doc.data();
  let imageUrls = [];
  if (data.images && Array.isArray(data.images)) {
    imageUrls = data.images;
  } else if (data.images && typeof data.images === "string") {
    imageUrls = [data.images];
  }

  // Ensure createdAt is a Firestore Timestamp before converting
  const createdAtDate = data.createdAt instanceof Timestamp
    ? data.createdAt.toDate()
    : (data.createdAt ? new Date(data.createdAt) : new Date()); // Fallback if not a Timestamp

  return {
    id: doc.id,
    ...data,
    imageUrls, // Keep original name if used elsewhere
    images: imageUrls, // Standardize to 'images' if possible
    helpedCount: data.helpedCount || 0,
    createdAt: data.createdAt, // Keep the original timestamp for scoring
    createdAtDate: createdAtDate, // Store the Date object
    isPromoted: data.isPromoted || false,
  };
};


// --- NEW: Marketing Score Calculation ---
export const calculateOfferScore = (offer, followingList = []) => {
  // --- Define Weights (Adjust these values based on desired importance) ---
  const WEIGHT_LIKES = 0.4; // Importance of likes
  const WEIGHT_RECENCY = 0.3; // Importance of how new the offer is
  const WEIGHT_FOLLOWING = 0.2; // Bonus for following the company
  const WEIGHT_PROMOTED = 0.1; // Bonus for promoted posts
  const MAX_RECENCY_SCORE_DAYS = 30; // Offers older than this get minimal recency score

  // --- Calculate Factors ---

  // 1. Likes Score (Logarithmic scale to prevent extreme values)
  const likesScore = Math.log10((offer.helpedCount || 0) + 1); // Add 1 to avoid log(0)

  // 2. Recency Score (Higher score for newer items)
  let recencyScore = 0;
  if (offer.createdAt && offer.createdAt.toDate) { // Check if it's a Firestore Timestamp
      const offerDate = offer.createdAt.toDate();
      const now = new Date();
      const ageInHours = (now.getTime() - offerDate.getTime()) / (1000 * 60 * 60);
      const ageInDays = ageInHours / 24;

      // Score decreases linearly over MAX_RECENCY_SCORE_DAYS
      recencyScore = Math.max(0, 1 - (ageInDays / MAX_RECENCY_SCORE_DAYS));
  } else {
      console.warn(`Offer ${offer.id} missing valid createdAt timestamp.`);
  }


  // 3. Following Bonus
  const followingBonus = (followingList.includes(offer.uid)) ? 1 : 0;

  // 4. Promoted Bonus
  const promotedBonus = offer.isPromoted ? 1 : 0; // Assumes an 'isPromoted' boolean field in offer data

  // --- Calculate Final Score ---
  const score =
    (likesScore * WEIGHT_LIKES) +
    (recencyScore * WEIGHT_RECENCY) +
    (followingBonus * WEIGHT_FOLLOWING) +
    (promotedBonus * WEIGHT_PROMOTED);

  // Add a small decay based purely on time to slightly differentiate items with same score
  const timeDecay = (offer.createdAt?.seconds || 0) / 1e11; // Small factor based on timestamp

  return score + timeDecay;
};

export const fetchQuestionsAndAnswers = (offerId, setQuestions) => {
  const q = query(
    collection(db, "questions"),
    where("offerId", "==", offerId),
    orderBy("createdAt", "asc")
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const fetchedQuestions = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setQuestions(fetchedQuestions);
  });

  return unsubscribe;
};

export const postQuestion = async (offerId, userId, questionText) => {
  try {
    await addDoc(collection(db, "questions"), {
      offerId,
      userId,
      question: questionText,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error al publicar la pregunta: ", error);
  }
};

export const postAnswer = async (questionId, userId, answerText, offerId) => {
  try {
    // Asumiendo que tienes una colección 'answers' para almacenar respuestas
    await addDoc(collection(db, "answers"), {
      questionId,
      userId,
      offerId,
      answer: answerText,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error al publicar la respuesta: ", error);
  }
};

export const fetchAnswers = (offerId, setAnswers) => {
  const q = query(
    collection(db, "answers"),
    where("offerId", "==", offerId),
    orderBy("createdAt", "asc")
  );

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const fetchedAnswers = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setAnswers(fetchedAnswers);
  });

  return unsubscribe;
};

export const fetchQuestionsAndAnswersWithUserData = (
  offerId,
  setQuestionsAndAnswers
) => {
  const q = query(
    collection(db, "questions"),
    where("offerId", "==", offerId),
    orderBy("createdAt", "asc")
  );

  const unsubscribe = onSnapshot(q, async (snapshot) => {
    const questionsAndAnswers = [];
    for (const questionDoc of snapshot.docs) {
      const questionData = questionDoc.data();

      // Fetch user data for the question
      const userDocRef = doc(db, "users", questionData.userId);
      const userDocSnap = await getDoc(userDocRef);
      const userData = userDocSnap.data();

      // Fetch answers for the question
      const answersQuery = query(
        collection(db, "answers"),
        where("questionId", "==", questionDoc.id)
      );
      const answersSnapshot = await getDocs(answersQuery);
      const answers = await Promise.all(
        answersSnapshot.docs.map(async (answerDoc) => {
          const answerData = answerDoc.data();

          // Fetch user data for the answer
          const answerUserDocRef = doc(db, "users", answerData.userId);
          const answerUserDocSnap = await getDoc(answerUserDocRef);
          const answerUserData = answerUserDocSnap.data();

          return {
            id: answerDoc.id,
            ...answerData,
            displayName: answerUserData.displayName,
            profileImageUrl: answerUserData.profileImageUrl,
          };
        })
      );

      questionsAndAnswers.push({
        id: questionDoc.id,
        ...questionData,
        displayName: userData.displayName,
        profileImageUrl: userData.profileImageUrl,
        answers: answers,
      });
    }

    setQuestionsAndAnswers(questionsAndAnswers);
  });

  return unsubscribe;
};

export const isCouponAvailable = async (cupon, userId) => {
  try {
    const usedCouponsRef = collection(db, "usedCoupons");
    const q = query(
      usedCouponsRef,
      where("cupon", "==", cupon),
      where("userId", "==", userId)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.empty;
  } catch (error) {
    console.error("Error al verificar cupón:", error);
    return false;
  }
};

// Procesa el QR escaneado
export const handleQRScan = async (qrData) => {
  try {
    // Si el QR es un string JSON, intentar parsearlo
    let processedData = qrData;
    if (typeof qrData === 'string') {
      try {
        processedData = JSON.parse(qrData);
      } catch {}
    }

    // Si es un QR de pago
    if (processedData.type === 'payment') {
      return {
        isValid: true,
        data: {
          type: 'payment',
          merchantId: processedData.merchantId,
          amount: processedData.amount,
          storeName: processedData.storeName,
          timestamp: processedData.timestamp
        }
      };
    }

    // Si es un QR de cupón
    const offersRef = collection(db, "offers");
    const q = query(offersRef, where("cupon", "==", processedData));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const offerDoc = querySnapshot.docs[0];
      const offerData = offerDoc.data();
      
      return {
        isValid: true,
        data: {
          offerId: offerDoc.id,
          cupon: offerData.cupon,
          discount: offerData.offer,
          storeName: offerData.name,
          product: offerData.product,
          description: offerData.description,
          originalPrice: offerData.originalPrice || 0,
          image: offerData.image || null
        }
      };
    }
    
    return {
      isValid: false,
      error: "Código QR no válido"
    };
  } catch (error) {
    console.error('Error procesando QR:', error);
    return {
      isValid: false,
      error: "Error al procesar el código QR"
    };
  }
};

// Procesa el pago con el cupón
export const processPaymentWithCoupon = async (offerData, cardData, userId) => {
  try {
    // Verificar si el cupón ya fue usado
    const isAvailable = await isCouponAvailable(offerData.cupon, userId);
    if (!isAvailable) {
      return {
        success: false,
        error: "Este cupón ya ha sido utilizado"
      };
    }

    // Calcular el descuento
    const discountAmount = (offerData.originalPrice * offerData.discount) / 100;
    const finalPrice = offerData.originalPrice - discountAmount;

    // Registrar uso del cupón
    await addDoc(collection(db, "usedCoupons"), {
      userId,
      cupon: offerData.cupon,
      offerId: offerData.offerId,
      discount: offerData.discount,
      originalPrice: offerData.originalPrice,
      finalPrice,
      usedAt: serverTimestamp()
    });

    // Registrar el pago
    await addDoc(collection(db, "payments"), {
      userId,
      offerId: offerData.offerId,
      cupon: offerData.cupon,
      cardId: cardData.id,
      discount: offerData.discount,
      originalPrice: offerData.originalPrice,
      finalPrice,
      storeName: offerData.storeName,
      timestamp: serverTimestamp(),
      status: "completed"
    });

    // Actualizar estadísticas
    const offerRef = doc(db, "offers", offerData.offerId);
    await updateDoc(offerRef, {
      usedCount: increment(1)
    });

    return {
      success: true,
      message: "Pago procesado exitosamente"
    };
  } catch (error) {
    console.error("Error en el pago:", error);
    return {
      success: false,
      error: "Error al procesar el pago"
    };
  }
};